import React, { Dispatch, SetStateAction } from 'react';
import Dropzone, { IDropzoneProps, defaultClassNames } from 'react-dropzone-uploader';
import { getDroppedOrSelectedFiles } from 'html5-file-selector';

import useToast from 'hooks/useToast';
import { CONTENT_TYPE, MAX_FILE_UPLOAD_2GB } from '../constants';
import { ISelectedHTML5File } from '../types';
import NSDropzoneInputComponent from './NSDropzoneInputComponent/NSDropzoneInputComponent';
import './NSDropzone.scss';

export interface INSDropzoneProps extends IDropzoneProps {
    pendingFiles?: { id: number, file: ISelectedHTML5File }[];
    setPendingFiles?: Dispatch<SetStateAction<{ id: number, file: ISelectedHTML5File }[]>>;
    dragAndDropText?: string;
}

const NSDropzone = ({
    pendingFiles,
    setPendingFiles,
    maxSizeBytes,
    multiple = true,
    accept,
    dragAndDropText,
    ...rest
}: INSDropzoneProps) => {
    const { showWarning, showError } = useToast();
    const getFilesFromEvent: any = (e: React.DragEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>) => new Promise(resolve => {
        getDroppedOrSelectedFiles(e).then((chosenFiles: ISelectedHTML5File[]) => {
            // If multiple is false, only allow the first file to be selected
            const filteredChosenFiles = multiple ? chosenFiles : chosenFiles.slice(0, 1);

            const hasFilesOverLimit = filteredChosenFiles.some(file => file.size > maxSizeBytes);
            const allowedTypes: Array<string> = accept === '*' ? Object.values(CONTENT_TYPE) : accept.split(', ');
            const droppedFileTypes = filteredChosenFiles.map(file => file.type.toLowerCase());
            const isFileTypeAllowed = (type: string) => allowedTypes.includes(type);

            if (hasFilesOverLimit) {
                const maxSizeMB = (maxSizeBytes ?? MAX_FILE_UPLOAD_2GB) / (1024 * 1024);
                const label = maxSizeMB >= 1024 ? `${(maxSizeMB / 1024).toFixed(0)}GB` : `${maxSizeMB.toFixed(0)}MB`;
                showWarning(`Unable to upload files over ${label}`);
            } else if (!droppedFileTypes.every(isFileTypeAllowed)) {
                showError('Document type not supported');
            } else {
                if (pendingFiles && setPendingFiles) {
                    const startIndex = pendingFiles.length ? pendingFiles[pendingFiles.length - 1].id : 0;
                    const finalChosenFiles = filteredChosenFiles.filter(file => file.fullPath !== '.DS_Store')
                        .map((file, idx) => ({ id: startIndex + idx + 1, file }));
                    setPendingFiles(prev => [...prev, ...finalChosenFiles]);
                }
                resolve(filteredChosenFiles.map(f => f.fileObject));
            }
        });
    });

    return (
        <Dropzone
            {...rest}
            accept={accept}
            InputComponent={props => <NSDropzoneInputComponent {...props} text={dragAndDropText} />}
            PreviewComponent={() => <span />}
            maxSizeBytes={maxSizeBytes}
            getFilesFromEvent={getFilesFromEvent}
            multiple={multiple}
            classNames={{
                ...defaultClassNames,
                dropzone: `${defaultClassNames.dropzone} NSDropzone`,
                dropzoneActive: 'NSDropzone__active',
            }}
        />
    );
};

export default NSDropzone;

NSDropzone.defaultProps = {
    ...Dropzone.defaultProps,
    inputContent: '',
    maxSizeBytes: MAX_FILE_UPLOAD_2GB,
};
